<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';

import NewContactSidePanelForm from 'dashboard/components-next/Contacts/ContactsForm/NewContactSidePanelForm.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['create', 'close']);

const { t } = useI18n();

const isOpen = ref(false);
const contact = ref(null);
const contactsFormRef = ref(null);

const uiFlags = useMapGetter('contacts/getUIFlags');
const isCreatingContact = computed(() => uiFlags.value.isCreating);
const newContactTitle = computed(() =>
  t('CONTACTS_LAYOUT.NEW_CONTACT_PANEL.TITLE')
);

const open = () => {
  isOpen.value = true;
};

const close = () => {
  isOpen.value = false;
  contact.value = null;
  contactsFormRef.value?.resetForm();
  emit('close');
};

const updateContact = contactPayload => {
  contact.value = contactPayload;
};

const submit = () => {
  if (!contact.value) return;
  emit('create', contact.value);
};

const onSuccess = () => {
  close();
};

defineExpose({
  open,
  close,
  onSuccess,
  contactsFormRef,
});
</script>

<template>
  <div v-if="isOpen" class="fixed inset-0 z-[70]">
    <div aria-hidden="true" class="absolute inset-0 bg-[rgba(24,24,27,0.65)]" />
    <section
      class="absolute right-0 top-0 flex h-full w-full max-w-[452px] flex-col overflow-hidden rounded-l-2xl border-l border-n-weak bg-n-surface-1 shadow-2xl"
      role="dialog"
      aria-modal="true"
      :aria-label="newContactTitle"
      @click.stop
    >
      <header
        class="flex h-12 items-center justify-between border-b border-n-weak px-4"
      >
        <div
          class="flex items-center gap-2 text-sm font-semibold text-n-slate-12"
        >
          <span class="i-lucide-user-plus size-5" />
          <span>{{ newContactTitle }}</span>
        </div>
        <button
          type="button"
          class="flex size-8 items-center justify-center rounded-md text-n-slate-11 hover:bg-n-alpha-2"
          @click="close"
        >
          <span
            class="i-lucide-x inline-flex h-5 w-5 min-h-5 min-w-5 shrink-0 items-center justify-center text-[20px] leading-none"
          />
        </button>
      </header>
      <div class="flex-1 overflow-y-auto">
        <NewContactSidePanelForm
          ref="contactsFormRef"
          @update="updateContact"
        />
      </div>
      <footer
        class="flex h-14 items-center justify-end gap-2 border-t border-n-weak bg-n-surface-1 px-4"
      >
        <Button
          :label="$t('DIALOG.BUTTONS.CANCEL')"
          variant="link"
          @click="close"
        />
        <Button
          :label="
            $t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.SAVE_CONTACT')
          "
          color="blue"
          :disabled="contactsFormRef?.isFormInvalid || isCreatingContact"
          :is-loading="isCreatingContact"
          @click="submit"
        />
      </footer>
    </section>
  </div>
</template>
